// react and external-libs
import React, { useCallback, useEffect, useMemo, useState } from 'react';
// redux
import { useAppSelector } from '../../../../app/hooks';
// interface
import { Survey } from '../../../../interfaces/survey.interface';
import { AssessmentSurvey, AssessmentNotification } from '../../../../interfaces/assessment.interface';
import User, { Company, CompanyApplication, Contact, Badge } from '../../../../interfaces/users.interface';
// services
import { fetchSurvey } from '../../../../services/surveys';
import { getHighestAchievement, fetchGoldBadgeLabel, fetchBlueBadgeLabel, BLUE_BADGE_LABEL, GOLD_BADGE_LABEL, SILVER_BADGE_LABEL, GOLD_NEXT_GEN_BADGE_LABEL, GOLD_STAR_BADGE_LABEL, selectBadgeFromArray } from '../../../../services/badge';
import { fetchAssessmentSurvey } from '../../../../services/assessments';
import { selectDisplayWorkflow, sortSiteWorkflowsForDisplay } from './siteWorkflowDisplay';
// components
import SiteName from './SiteName';
import GetBadge from '../../GetBadge';
import WorkflowStatus from '../../WorkflowStatus';
import WorkflowButtons from '../../WorkflowButtons';
import { DataCell, DataRow } from '../../../Tables/DataList';
import ChipsWithOverflow from '../../../Layout/ChipsWithOverflow';
import AssessmentNotificationsAlertIcon from '../../AssessmentNotificationsAlertIcon';
import { useTheme } from '@mui/material/styles';
import AnimatedSidebarIcon from '../../AnimatedSidebarIcon';

type SiteDataRowProps = {
  company: Company;
  site: Contact;
  userCanAdminCompany: boolean;
  companySuccessCallback: any;
  showCanBeginNewWorkflow?: boolean;
  setCanStartNewQuestionniare: (canStartNewQuestionniare: boolean) => void;
  onClick: (site: Contact) => void;
  /** Active Sites-tab status filter: the row then represents the questionnaire that matched it. */
  statusFilter?: string;
};

const SiteDataRow = (props: SiteDataRowProps) => {
  // props
  const { company, site, onClick, setCanStartNewQuestionniare, showCanBeginNewWorkflow, statusFilter } = props;
  // redux
  const { workflows: companyWorkflows } = useAppSelector((state) => state.workflow);
  // states
  const [cSite, setcSite] = useState(props.site);
  const [curSurvey, setCurSurvey] = useState<Survey | undefined>();
  const [assessmentSurvey, setAssessmentSurvey] = useState<AssessmentSurvey | undefined>();
  // local variables
  const workflows = useMemo(
    () => sortSiteWorkflowsForDisplay(companyWorkflows?.filter((wf) => wf.site === site.id) || []),
    [companyWorkflows, site.id],
  );

  const workflow = useMemo(() => selectDisplayWorkflow(workflows, statusFilter), [workflows, statusFilter]);
  // Badge labels caption the shield, so both read the displayed workflow.
  const displayedWorkflows = useMemo(() => (workflow ? [workflow] : []), [workflow]);
  const highestAchievementBlue = useMemo(() => getHighestAchievement(displayedWorkflows, [BLUE_BADGE_LABEL]), [displayedWorkflows]);
  const highestAchievementGold = useMemo(() => getHighestAchievement(
    displayedWorkflows,
    [
      GOLD_BADGE_LABEL,
      SILVER_BADGE_LABEL,
      GOLD_NEXT_GEN_BADGE_LABEL,
      GOLD_STAR_BADGE_LABEL,
    ],
  ), [displayedWorkflows]);
  const companyCertification = useMemo(
    () => cSite?.certifications?.[0]
      ?? company?.companyCertifications?.find((cert) => cert.sites?.find((tsite) => tsite.id === cSite.id)),
    [cSite?.certifications, company?.companyCertifications, cSite?.id],
  );
  const serviceNames = useMemo(() => cSite?.services?.map((service) => service.name), [cSite?.services]);
  const applicationNames = useMemo(
    () =>
      cSite?.companyApplications?.map((ca: CompanyApplication) => {
        if (ca.application?.name) return ca.application.name;
        const companyApplication = company?.companyApplications?.find((compApp) => compApp.id === ca.id);
        return companyApplication?.application?.name ?? 'Name Missing';
      }),
    [company?.companyApplications, cSite?.companyApplications],
  );

  // Status is passed in so the guard reads the workflow being fetched, not first render's.
  const handleFetchAssessmentSurvey = useCallback(async (assessmentSurveyId: number, workflowStatus?: string) => {
    const assSurvey = await fetchAssessmentSurvey(assessmentSurveyId, true);
    if (workflowStatus !== 'Complete' || assSurvey.bestPracticeRemediationCount) {
      setAssessmentSurvey(assSurvey);
    }
  }, []);

  const handleFetchSurvey = useCallback(async (surveyId: number) => {
    const surveyData = await fetchSurvey(surveyId);
    setCurSurvey(surveyData);
  }, []);

  // The filter can swap the row onto another questionnaire; drop the previous one's surveys.
  useEffect(() => {
    setAssessmentSurvey(undefined);
    setCurSurvey(undefined);
  }, [workflow?.id]);

  // loads the survey?
  useEffect(() => {
    if (workflow?.assessmentSurvey) {
      // fetch assessment survey
      handleFetchAssessmentSurvey(workflow?.assessmentSurvey, workflow?.status);
      return;
    }

    // we have a bp survey and no assessment
    if (
      workflow?.bpSurvey &&
      !workflow?.assessment &&
      !['submittedForAssessment', 'complete'].includes(workflow?.status)
    ) {
      // fetch survey
      handleFetchSurvey(workflow?.bpSurvey);
      return;
    }
  }, [workflow]);

  // setting of site from props
  useEffect(() => {
    setcSite(site);
  }, [site]);

  const blueBadgeLabel = useMemo(() => highestAchievementBlue?.badge && fetchBlueBadgeLabel([highestAchievementBlue.badge], highestAchievementBlue.version) || '', [highestAchievementBlue]);
  const goldBadgeLabel = useMemo(() => highestAchievementGold?.badge && fetchGoldBadgeLabel([highestAchievementGold.badge], highestAchievementGold.version) || '', [highestAchievementGold]);
  const [assessmentNotifications, setAssessmentNotifications] = useState<AssessmentNotification[]>([]);

  const handleSetCanStartNewQuestionniare = useCallback(() => {
    setCanStartNewQuestionniare(true);
    onClick(site);
  }, [setCanStartNewQuestionniare, onClick, site]);

  const theme = useTheme();

  return (
    <>
      <DataRow onClick={() => onClick(site)} hover
        sx={{
          cursor: 'pointer',
          position: 'relative',
          flexWrap: 'nowrap',
          '&:hover': {
            '& .slide-icon': {
              fill: theme.palette.common.white,
              color: theme.palette.common.white, // ← Use color, not fill
            },
            '& .hoverBox': {
              // Grey background
              backgroundColor: theme.palette.primary.main,
              transform: 'translateX(-4px)',
            },
          },
        }}>
        <DataCell xs={0.75} minWidth={90} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <GetBadge
            shield={workflow && selectBadgeFromArray(workflow.badges as Badge[], true)}
            isActive={company.isPublished}
            goldBadgeLabel={highestAchievementGold?.badge && fetchGoldBadgeLabel([highestAchievementGold.badge], highestAchievementGold.version, highestAchievementGold.badge.title)}
            blueBadgeLabel={blueBadgeLabel}
            assessmentSurvey={assessmentSurvey}
            assessor={workflow?.assignedAssessor as User | undefined}
            certification={companyCertification}
            company={company.id}
          />
        </DataCell>
        <DataCell xs={2}>
          <SiteName contact={cSite} />
        </DataCell>
        <DataCell
          xs={2}
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.25rem',
            padding: '0.25rem',
          }}
        >
          {ChipsWithOverflow(applicationNames, 1, 'app')}
        </DataCell>
        <DataCell
          xs={2}
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.25rem',
            padding: '0.25rem',
          }}
        >
          {ChipsWithOverflow(serviceNames, 1, 'service')}
        </DataCell>
        <DataCell xs={3}>
          <>
            <AssessmentNotificationsAlertIcon assessmentNotifications={assessmentNotifications} />
            <WorkflowStatus
              hasGoldExpired={goldBadgeLabel.includes('Expired')}
              hasBlueExpired={blueBadgeLabel.includes('Expired')}
              workflowDisplayType={workflow?.workflowType}
              workflowDisplayStatusObject={workflow?.displayStatus}
            />
          </>
        </DataCell>
        <DataCell xs={1.75} minWidth={200} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <WorkflowButtons
            company={company}
            workflow={workflow}
            survey={curSurvey}
            assessmentSurvey={assessmentSurvey}
            site={cSite}
            showCanBeginNewWorkflow={showCanBeginNewWorkflow}
            setAssessmentNotifications={setAssessmentNotifications}
            setCanStartNewQuestionniare={handleSetCanStartNewQuestionniare}
          />
        </DataCell>
        <DataCell xs={0.5} sx={{ display: 'flex', justifyContent: 'center', pr: 2 }}>
          <AnimatedSidebarIcon />
        </DataCell>
      </DataRow>
    </>
  );
};

export default SiteDataRow;
